import { DraftCollectible } from "@/types/collectibles"
import { Blog, Note } from "@types"
import {
  COLLECTIBLE_DATA,
  getCollectibleChain,
  getCollectibleCost,
  getCollectibleSupply,
} from "@/util/crypto"
import { parseEther } from "viem"

import { getCollectors } from "@/lib/api/collectibles"
import { getWalletName } from "@/lib/utils/wallet"

/**
 * Gets the default metadata for a new collectible, given a user's
 * blog and post.
 *
 * This is everything that is needed to mint a new NFT.
 */
export function newCollectibleMetadata(blog: Blog, post: Note) {
  const costEth = getCollectibleCost(post, blog).toString()
  const supply = getCollectibleSupply(post, blog)
  const chain = getCollectibleChain(post, blog)

  const collectible: DraftCollectible = {
    blogId: blog.id,
    noteId: post.id,
    createdAt: Date.now(),
    type: "POST",
    status: "PENDING",
    // We set this here but it's overridden on the backend.
    version: 1,
    text: post.title || blog.name || "Untitled NFT",
    position: {
      from: 0,
      to: 0,
    },
    costEth,
    supply,
    chain,
  }

  const ownerAddress =
    post?.collectibleWalletAddress ||
    blog.collectibleWalletAddress ||
    (blog.user && "wallet_address" in blog.user ? blog.user.wallet_address : "")

  const symbol = blog?.lowercase_url?.substring(1).toUpperCase() || "PARAGRAPH"

  const contractMetadata = {
    name: blog.name,
    symbol,
    ownerAddress: ownerAddress as `0x${string}`,
    supply: BigInt(supply),
    costWei: parseEther(costEth),
    position: collectible.position,
    chain: chain,
  }

  const factoryAddress = COLLECTIBLE_DATA[chain].contractAddress

  return { collectible, contractMetadata, factoryAddress }
}

export async function getRecentOrFirstCollector(
  sortOrder: "asc" | "desc",
  collectibleId: string
) {
  const collectors = await getCollectors(
    collectibleId,
    1,
    undefined,
    undefined,
    sortOrder
  )

  const data = collectors?.[0]
  if (!data) return null

  let displayName

  if ("authorName" in data.user && data.user.authorName) {
    displayName = data.user.authorName
  } else {
    let walletName = await getWalletName(data.walletAddress as `0x${string}`)
    displayName = walletName.name
  }

  const imageUrl =
    "avatar_url" in data.user && data.user.avatar_url
      ? data.user.avatar_url
      : ""

  return { imageUrl, displayName, data }
}
